import { MeasureController } from "../../MeasureController";
import { JudgmentResult, JudgmentResultType, JudgmentResultEnum } from "../../types.client";
import { OpticalLevelMeasure } from "../../../models/schemas/measures/communication.schema";
import { OPTICAL_LEVEL_THRESHOLDS } from "../../constants/judgmentCriteria";

/**
 * 광 송수신 레벨 컨트롤러
 * 판정 기준 (OPTICAL_LEVEL_THRESHOLDS 사용):
 * - 송신기: ≥tx.min (0dBm)
 * - 수신기: rx.min ~ rx.max (-8dBm ~ -3dBm)
 */
export class OpticalLevelController extends MeasureController<OpticalLevelMeasure> {
  protected getDefaultData(): OpticalLevelMeasure {
    return {
      seq: 0,
      deviceType: "",
      measuredValue: 0,
      unit: "dBm",
      result: null,
      discussion: "",
      remark: "",
      photo: { checkUrl: "", resultUrl: "" },
    };
  }

  protected judge(): JudgmentResult {
    const { deviceType, measuredValue } = this.data;

    // 데이터 미입력 시
    if (!deviceType || measuredValue === undefined || measuredValue === null) {
      return {
        result: JudgmentResultEnum.NA,
        discussion: this.generateDiscussion(JudgmentResultEnum.NA),
      };
    }

    // 장치 유형에 따른 판정
    const isTransmitter = deviceType.includes("송신기");
    const isReceiver = deviceType.includes("수신기");

    let result: JudgmentResultType;

    const { tx, rx } = OPTICAL_LEVEL_THRESHOLDS;
    if (isTransmitter) {
      // 송신기: ≥tx.min
      result = measuredValue >= tx.min ? JudgmentResultEnum.GOOD : JudgmentResultEnum.BAD;
    } else if (isReceiver) {
      // 수신기: rx.min ~ rx.max
      result =
        measuredValue >= rx.min && measuredValue <= rx.max
          ? JudgmentResultEnum.GOOD
          : JudgmentResultEnum.BAD;
    } else {
      // 구분 불명확
      return {
        result: JudgmentResultEnum.NA,
        discussion: "장치 유형 구분 불명확 (송신기/수신기)",
      };
    }

    return {
      result,
      discussion: this.generateDiscussion(result),
    };
  }

  protected generateDiscussion(result: JudgmentResultType): string {
    if (result === JudgmentResultEnum.NA) {
      return "데이터 미입력";
    }

    const { deviceType, measuredValue } = this.data;
    const isTransmitter = deviceType?.includes("송신기");
    const { tx, rx } = OPTICAL_LEVEL_THRESHOLDS;

    if (result === JudgmentResultEnum.GOOD) {
      return isTransmitter
        ? `광 송신기 레벨이 기준값(≥${tx.min}dBm) 이상으로 정상`
        : `광 수신기 레벨이 정상 범위(${rx.min}~${rx.max}dBm) 내에 있음`;
    }

    return isTransmitter
      ? `광 송신기 레벨이 기준값 미달 (${measuredValue}dBm < ${tx.min}dBm)`
      : `광 수신기 레벨이 정상 범위를 벗어남 (${measuredValue}dBm, 정상: ${rx.min}~${rx.max}dBm)`;
  }
}
